import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { PublicationDetail, PublicationItem } from '../../shared/api/types';
import { Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useTabParam } from '../../shared/ui/useTabParam';
import { useAsync } from '../../shared/ui/useAsync';
import { KIND } from '../content/labels';
import { Reading } from '../content/PiecePage';
import { FollowUpSection } from '../content/FollowUp';
import { useMe } from '../session/SessionProvider';

export const publishedOn = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });

/** Lista de publicaciones: la usan la iglesia (con sesión) y su página pública. */
export function PublicationList({ items, linkTo }: { items: PublicationItem[]; linkTo: (p: PublicationItem) => string }) {
  return (
    <ul className="list card">
      {items.map((p) => (
        <li key={p.id} className="list__item">
          <Link className="list__link" to={linkTo(p)}>
            <span className="muted small">
              {KIND[p.kind]} · {publishedOn(p.publishAt)}
            </span>
            <strong>{p.title}</strong>
            {p.summary ? <span className="small">{p.summary}</span> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

const FILTERS = [
  ['todo', 'Todo', null],
  ['predicaciones', 'Predicaciones', 'sermon'],
  ['estudios', 'Estudios', 'study'],
  ['articulos', 'Artículos', 'article'],
] as const;
const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Publicación (Motor 23): lo que la iglesia ha publicado para su gente. */
export function PublicationsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [filter, setFilter] = useTabParam(FILTERS.map(([k]) => k), 'tipo');
  const [q, setQ] = useState('');
  const feed = useAsync(
    (signal) => (churchId ? api.get<{ items: PublicationItem[] }>(`/publishing/churches/${churchId}/feed`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Publicaciones</h1>
        <p className="muted">Predicaciones, artículos y estudios de tu iglesia.</p>
      </header>
      {feed.status === 'loading' ? <Loading /> : null}
      {feed.status === 'error' ? <ErrorState error={feed.error} onRetry={feed.reload} /> : null}
      {feed.status === 'ready' && feed.data.length === 0 ? <EmptyState title="Aún no hay publicaciones." /> : null}
      {feed.status === 'ready' && feed.data.length > 0 ? (
        <>
          <div className="tabs tabs--scroll" role="tablist" aria-label="Tipo de publicación">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={filter === key} className={`tabs__tab${filter === key ? ' tabs__tab--active' : ''}`} onClick={() => setFilter(key)}>
                {label}
              </button>
            ))}
          </div>
          <Field label="Buscar por título" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Escribe parte del título" autoComplete="off" />
          {(() => {
            const kind = FILTERS.find(([k]) => k === filter)![2];
            const shown = feed.data.filter((p) => (!kind || p.kind === kind) && (!q.trim() || fold(p.title).includes(fold(q.trim()))));
            return shown.length ? <PublicationList items={shown} linkTo={(p) => `/app/publicaciones/${p.id}`} /> : <p className="muted">No hay publicaciones con ese filtro.</p>;
          })()}
        </>
      ) : null}
    </div>
  );
}

export function PublicationArticle({ publication: p, linkPassage = false }: { publication: PublicationDetail; linkPassage?: boolean }) {
  return (
    <>
      <header className="page__header">
        <p className="muted small">
          {KIND[p.kind]} · {publishedOn(p.publishAt)}
        </p>
        <h1>{p.title}</h1>
      </header>
      <Card>
        <Reading revision={p} linkPassage={linkPassage} />
      </Card>
      {p.followUp ? <FollowUpSection title={p.title} followUp={p.followUp} /> : null}
    </>
  );
}

export function PublicationPage() {
  const { publicationId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const pub = useAsync(
    (signal) => (churchId ? api.get<PublicationDetail>(`/publishing/churches/${churchId}/publications/${publicationId}`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, publicationId],
  );
  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/publicaciones">← Publicaciones</Link>
      </p>
      {pub.status === 'loading' ? <Loading /> : null}
      {pub.status === 'error' ? <ErrorState error={pub.error} onRetry={pub.reload} /> : null}
      {pub.status === 'ready' ? <PublicationArticle publication={pub.data} linkPassage /> : null}
    </div>
  );
}
